import i18n from "@/plugins/i18n";
import { DataOptions } from "vuetify";
import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { filterCapitalize } from "@/filters/capitalize";
import {
  GDS,
  GDSService,
  GDSSettings,
  GDSFormOfPayment,
  GDSServiceResponse,
} from "@/models/api/administration/gds/gds";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import {
  getAdministrationGDSList,
  getGDSFormsOfPayment,
  getGDSServices,
  getGDSSettings,
  toggleGDS,
  toggleGDSSettings,
  toggleGDSFormOfPayment,
  toggleGDSIsCostDocumentRequired,
} from "@/requests/administration/administrationGDS";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";

export interface AdministrationGDSInterface {
  //GDS
  isGDSLoading: boolean;
  gdsItems: Array<GDS>;
  //GDS Settings
  isServiceLoading: boolean;
  serviceFilters: null | Array<CriterionItemRequest>;
  serviceOptions: DataOptions;
  serviceCount: number;
  serviceItems: Array<GDSService>;
  selectedService: GDSService | null;
  isSettingsLoading: boolean;
  settingsItems: Array<GDSSettings>;
  selectedSettings: GDSSettings | null;
  isFormOfPaymentLoading: boolean;
  formOfPaymentItems: Array<GDSFormOfPayment>;
}

export const AdministrationGDSState: AdministrationGDSInterface = {
  //GDS
  isGDSLoading: false,
  gdsItems: [],
  //GDS Settings
  isServiceLoading: false,
  serviceFilters: null,
  serviceOptions: new DefaultDataOptions(),
  serviceCount: 0,
  serviceItems: [],
  selectedService: null,
  isSettingsLoading: false,
  settingsItems: [],
  selectedSettings: null,
  isFormOfPaymentLoading: false,
  formOfPaymentItems: [],
};

const AdministrationGDSModule = defineModule({
  state: AdministrationGDSState as AdministrationGDSInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsGDSLoading(state, param: boolean) {
      state.isGDSLoading = param;
    },
    setGDSItems(state, param: Array<GDS>) {
      state.gdsItems = param;
    },
    setIsServiceLoading(state, param: boolean) {
      state.isServiceLoading = param;
    },
    setServiceFilters(state, param: Array<CriterionItemRequest>) {
      state.serviceFilters = param;
    },
    setServiceOptions(state, param: DataOptions) {
      state.serviceOptions = param;
    },
    setServices(state, param: GDSServiceResponse) {
      state.serviceCount = param.totalCount;
      state.serviceItems = param.items;
    },
    setSelectedService(state, param: GDSService | null) {
      state.selectedService = param;
    },
    setIsSettingsLoading(state, param: boolean) {
      state.isSettingsLoading = param;
    },
    setSettings(state, param: Array<GDSSettings>) {
      state.settingsItems = param;
    },
    setSelectedSettings(state, param: GDSSettings | null) {
      state.selectedSettings = param;
    },
    setIsFormOfPaymentLoading(state, param: boolean) {
      state.isFormOfPaymentLoading = param;
    },
    setFormsOfPayment(state, param: Array<GDSFormOfPayment>) {
      state.formOfPaymentItems = param;
    },
  },
  actions: {
    async downloadGDSList(context) {
      const { commit } = _moduleActionContext(context);
      commit.setIsGDSLoading(true);
      const response = await getAdministrationGDSList();
      commit.setGDSItems(response);
      commit.setIsGDSLoading(false);
    },
    async toggleGDS(context, id: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      const result = (await rootDispatch.popup.addConfirm({
        title: filterCapitalize(i18n.t("warning")),
        content: filterCapitalize(i18n.t("areYouSureYouWantToChangeThisOption")),
        customButtons: [
          {
            text: filterCapitalize(i18n.t("no")),
            color: "warning",
            outlined: true,
            value: false,
          },
          {
            text: filterCapitalize(i18n.t("yes")),
            color: "warning",
            value: true,
          },
        ],
      })) as boolean;
      if (result) {
        await toggleGDS(id);
        await dispatch.downloadGDSList();
      }
    },
    async downloadGDSServices(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsServiceLoading(true);
      const response = await getGDSServices({
        count: state.serviceOptions.itemsPerPage,
        page: state.serviceOptions.page - 1,
        criterions: _.cloneDeep(state.serviceFilters),
        filterName: FILTER_NAMES.ServiceTypeForDocumentTypeFilter,
      });
      commit.setServices(response);
      commit.setIsServiceLoading(false);
      if (state.serviceItems[0] !== null) commit.setSelectedService(state.serviceItems[0]);
    },
    async downloadGDSSettings(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedService) {
        commit.setIsSettingsLoading(true);
        const response = await getGDSSettings(state.selectedService.id);
        commit.setSettings(response);
        commit.setIsSettingsLoading(false);
        if (state.settingsItems[0] !== null) commit.setSelectedSettings(state.settingsItems[0]);
      } else {
        commit.setSettings([]);
        commit.setSelectedSettings(null);
      }
    },
    async downloadGDSFormsOfPayment(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedService && state.selectedSettings) {
        commit.setIsFormOfPaymentLoading(true);
        const response = await getGDSFormsOfPayment(state.selectedService.id, state.selectedSettings.id);
        commit.setFormsOfPayment(response);
        commit.setIsFormOfPaymentLoading(false);
      } else {
        commit.setFormsOfPayment([]);
      }
    },
    async toggleGDSSettings(context, id: number) {
      const { state, dispatch } = _moduleActionContext(context);
      if (state.selectedService) {
        await toggleGDSSettings(state.selectedService.id, id);
        await dispatch.downloadGDSSettings();
      }
    },
    async toggleGDSIsCostDocumentRequired(context, id: number) {
      const { state, dispatch } = _moduleActionContext(context);
      if (state.selectedService) {
        await toggleGDSIsCostDocumentRequired(state.selectedService.id, id);
        await dispatch.downloadGDSSettings();
      }
    },
    async toggleGDSFormOfPayment(context, id: number) {
      const { state, dispatch } = _moduleActionContext(context);
      if (state.selectedService && state.selectedSettings) {
        await toggleGDSFormOfPayment(state.selectedService.id, state.selectedSettings.id, id);
        await dispatch.downloadGDSFormsOfPayment();
      }
    },
  },
});
export default AdministrationGDSModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationGDSModule);
